import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Alert, Badge, Button, Form } from 'react-bootstrap';

/**
 * Asked on a vendor bill (tax invoice or non-GST purchase) for goods with no GRN linked. Stock only comes in through a
 * GRN, and the vendor must be owed the goods once, so the bill says where its goods are: received with it (a GRN is
 * made from the bill into a warehouse) or still to arrive (the GRN is raised against this bill when they come, adding
 * stock but nothing more to the vendor's account).
 */
function GoodsReceiptChoice({ value, onChange, uninvoicedGrns = 0, document = 'invoice', idPrefix = 'goods' }) {
  const [warehouses, setWarehouses] = useState([]);

  useEffect(() => {
    axios.get('/api/purchase-invoices/warehouses')
      .then((r) => setWarehouses(Array.isArray(r.data) ? r.data : []))
      .catch(() => setWarehouses([]));
  }, []);

  // One warehouse: nothing to choose
  useEffect(() => {
    if (value.mode === 'RECEIVE_NOW' && !value.warehouseId && warehouses.length === 1) {
      onChange({ ...value, warehouseId: warehouses[0].id });
    }
  }, [value, warehouses, onChange]);

  return (
    <Alert variant="warning" className="small py-2 mb-2">
      <div className="mb-1">
        <strong>No goods receipt (GRN) is linked.</strong>{' '}
        {uninvoicedGrns > 0
          ? `This vendor has ${uninvoicedGrns} received GRN${uninvoicedGrns === 1 ? '' : 's'} not yet billed: if these goods came on one, tick it above. Otherwise, where are the goods?`
          : 'Where are the goods on this ' + document + '?'}
      </div>
      <Form.Check type="radio" id={`${idPrefix}-now`} name={`${idPrefix}-mode`}
        checked={value.mode === 'RECEIVE_NOW'} onChange={() => onChange({ ...value, mode: 'RECEIVE_NOW' })}
        label="Received with this bill: add them to stock now" />
      {value.mode === 'RECEIVE_NOW' && (
        <div className="ms-4 my-1 d-flex align-items-center gap-2 flex-wrap">
          <span>Into warehouse</span>
          <Form.Select size="sm" style={{ width: 'auto', minWidth: '12rem' }} value={value.warehouseId || ''}
            onChange={(e) => onChange({ ...value, warehouseId: e.target.value ? Number(e.target.value) : null })}>
            <option value="">Choose...</option>
            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </Form.Select>
          <span className="text-muted">Pick each item from the list and enter its quantity; a GRN is made from them.</span>
        </div>
      )}
      <Form.Check type="radio" id={`${idPrefix}-later`} name={`${idPrefix}-mode`}
        checked={value.mode === 'TO_ARRIVE'} onChange={() => onChange({ ...value, mode: 'TO_ARRIVE' })}
        label="Still to arrive: when they come, raise the GRN against this bill (it adds stock, not another payable)" />
    </Alert>
  );
}

/**
 * The goods choice checked before saving: a message to show, or null when the bill can be saved.
 * goodsLines are the bill's goods lines as the form holds them ({ description, itemId, quantity, unitOfMeasurementId }).
 */
export const goodsReceiptProblem = (value, goodsLines) => {
  if (value.mode === 'TO_ARRIVE') return null;
  if (value.mode !== 'RECEIVE_NOW') return 'Say where the goods are: received with this bill, or still to arrive';
  if (!value.warehouseId) return 'Choose the warehouse the goods went into';
  for (const l of goodsLines) {
    const what = l.description?.trim() || 'a goods line';
    if (!l.itemId) return `Pick the item for '${what}' from the list so it can go into stock`;
    if (!(Number(l.quantity) > 0)) return `Enter the quantity received for '${what}'`;
    if (!l.unitOfMeasurementId) return `'${what}' has no unit; pick it from the list again or add a unit to the item`;
  }
  return null;
};

export const emptyGoodsReceipt = () => ({ mode: '', warehouseId: null });

/** Where a bill's goods are, as a badge for lists and details; nothing when a GRN was simply linked. */
export function GoodsStatusBadge({ status, className = 'ms-1' }) {
  if (status === 'TO_ARRIVE') return <Badge bg="warning" text="dark" className={className} title="Raise the GRN against this bill when the goods come">Goods to arrive</Badge>;
  if (status === 'ARRIVED') return <Badge bg="info" className={className} title="The GRN was raised against this bill">Goods arrived</Badge>;
  if (status === 'RECEIVED_NOW') return <Badge bg="light" text="dark" className={`${className} border`} title="A GRN was made from this bill">Received with bill</Badge>;
  return null;
}

/**
 * On a bill's details: a bill recorded before bills said where their goods were (no goods status, no GRN) can be marked
 * as still waiting for them, so the GRN is raised against it when they come; undone while none have come.
 */
export function GoodsToArriveButton({ bill, onChanged }) {
  const [busy, setBusy] = useState(false);
  if (!bill || bill.status !== 'POSTED' || (bill.grnNumbers || []).length > 0) return null;
  const hasGoods = bill.documentKind !== 'NON_GST' || (bill.lines || []).some((l) => l.lineType === 'GOODS');
  const mark = !bill.goodsStatus && hasGoods;
  const unmark = bill.goodsStatus === 'TO_ARRIVE';
  if (!mark && !unmark) return null;

  const run = async () => {
    const question = mark
      ? `Mark the goods on ${bill.internalNumber} as still to arrive?\n\nWhen they come, raise their GRN against this bill: it adds the stock without owing the vendor again.`
      : `${bill.internalNumber} will no longer wait for goods, and a GRN for this vendor won't offer it.\n\nDo this only if its goods won't come in on a GRN.`;
    if (!window.confirm(question)) return;
    try {
      setBusy(true);
      await axios.post(`/api/purchase-invoices/${bill.id}/goods-to-arrive`, { toArrive: mark });
      toast.success(mark ? 'Marked as goods to arrive' : 'No longer waiting for goods');
      onChanged();
    } catch (error) {
      const data = error.response?.data;
      toast.error(typeof data === 'string' && data ? data : 'Could not change the bill');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button variant="outline-secondary" onClick={run} disabled={busy}
      title={mark ? 'For a bill recorded before its goods came' : 'Only while none of its goods have come in'}>
      {mark ? 'Goods still to arrive' : 'Not waiting for goods'}
    </Button>
  );
}

export default GoodsReceiptChoice;
